// Content Engine — templates + generated HTML media (docs/09 §B). The C-2 CMS
// surface: list first-party slide templates, render live previews, instantiate a
// template into an editable `html_template` media, and re-render its fields.
//
// These types intentionally live in the CMS (not @seencast/shared): the shared
// Media schema is frozen at image/video/url for Phase 1, so the extra template
// kinds and the `template_id`/`fields_json` columns are modelled here as a local
// widening until the shared schema catches up (contract question routed to main).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request, API_BASE, ApiError } from './client.ts';
import { getActiveOrgId } from './org-store.ts';
import { HTML_MEDIA_KINDS, MEDIA_KINDS } from '@seencast/shared';
import type { Media, MediaKind } from '@seencast/shared';
import type { ListEnvelope } from './client.ts';

// --- HTML media kinds -----------------------------------------------------------
// The two generated, self-contained sandboxed-iframe kinds (docs/09 §B1/§B3), now
// part of the shared Media schema. `isHtmlKind` narrows a plain string so callers
// can branch on it (e.g. in a switch) without a union import.
export type HtmlKind = (typeof HTML_MEDIA_KINDS)[number];

export function isHtmlKind(kind: string): kind is HtmlKind {
  return (HTML_MEDIA_KINDS as readonly string[]).includes(kind);
}

/** All kinds the Media grid can show/filter — the full shared media kind set. */
export const CONTENT_KINDS = MEDIA_KINDS;
export type ContentKind = MediaKind;

// --- Template documents ---------------------------------------------------------
export type TemplateFieldType = 'text' | 'multiline' | 'color' | 'image_media_id';

export interface TemplateField {
  key: string;
  label: string;
  type: TemplateFieldType;
  max_len?: number;
  default?: string;
}

export interface Template {
  id: string;
  name: string;
  description: string;
  fields: TemplateField[];
  aspect: '16:9';
}

/** The mutable field document stored on a template-backed media (`fields_json`).
 *  Delivered by the API as a PARSED object (never a JSON string). */
export type TemplateFields = Record<string, string>;

/** Alias for a template-backed media. The shared Media schema now carries
 *  `template_id` + `fields_json` directly, so no local widening is needed. */
export type TemplateMedia = Media;

/** Seed a fields document from a template's declared defaults. */
export function defaultFields(tpl: Template): TemplateFields {
  const out: TemplateFields = {};
  for (const f of tpl.fields) out[f.key] = f.default ?? '';
  return out;
}

// --- List -----------------------------------------------------------------------
export function listTemplates(): Promise<Template[]> {
  return request<ListEnvelope<Template>>('/templates').then((r) => r.items);
}

export function useTemplates() {
  return useQuery({ queryKey: ['templates'], queryFn: listTemplates, staleTime: 5 * 60_000 });
}

// --- Preview (text/html, not persisted) -----------------------------------------
/** POST fields to a template and get back the rendered HTML string. Bypasses the
 *  JSON `request()` helper because the response is `text/html`. */
export async function renderTemplatePreview(
  templateId: string,
  fields: TemplateFields,
  signal?: AbortSignal,
): Promise<string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const orgId = getActiveOrgId();
  if (orgId) headers['X-Org-Id'] = orgId;
  const res = await fetch(`${API_BASE}/templates/${templateId}/preview`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ fields }),
    signal,
  });
  if (!res.ok) throw new ApiError(res.status, 'preview_failed', 'Could not render preview');
  return res.text();
}

// --- Instantiate ----------------------------------------------------------------
export interface CreateFromTemplateBody {
  template_id: string;
  name: string;
  fields: TemplateFields;
}

export function useCreateFromTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFromTemplateBody) =>
      request<TemplateMedia>('/media/from-template', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media'] }),
  });
}

// --- Edit fields ----------------------------------------------------------------
export function usePatchMediaFields() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fields }: { id: string; fields: TemplateFields }) =>
      request<TemplateMedia>(`/media/${id}/fields`, { method: 'PATCH', body: { fields } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media'] }),
  });
}
